import { useMemo, useState } from "react";
import { useProjects, useTasks } from "../api";
import { ErrorBox, Empty, Loading } from "../components/State";

type View = "list" | "board" | "gantt";
const COLUMNS = ["backlog", "todo", "in_progress", "in_review", "done"] as const;

export default function Projects() {
  const projects = useProjects();
  const tasks = useTasks();
  const [selected, setSelected] = useState<string | "all">("all");
  const [view, setView] = useState<View>("list");

  const all = tasks.data?.tasks ?? [];
  const shown = useMemo(
    () => (selected === "all" ? all : all.filter((t) => t.projectId === selected)),
    [all, selected]
  );

  if (projects.loading || tasks.loading) return <Loading what="projects" />;
  if (projects.error) return <ErrorBox error={projects.error} what="projects" />;
  if (tasks.error) return <ErrorBox error={tasks.error} what="tasks" />;

  const list = projects.data?.projects ?? [];
  if (!list.length) return <Empty what="projects" />;

  // Gantt scale across the visible tasks
  const dated = shown.filter((t) => t.startAt && t.dueAt);
  const min = Math.min(...dated.map((t) => +new Date(t.startAt!)));
  const max = Math.max(...dated.map((t) => +new Date(t.dueAt!)));
  const span = Math.max(1, max - min);

  return (
    <>
      <div className="card">
        <h2>Projects</h2>
        <div className="row" style={{ flexWrap: "wrap", gap: 8 }}>
          <button className={`btn ${selected === "all" ? "primary" : ""}`} onClick={() => setSelected("all")}>
            All ({all.length})
          </button>
          {list.map((p) => (
            <button
              key={p.id}
              className={`btn ${selected === p.id ? "primary" : ""}`}
              onClick={() => setSelected(p.id)}
            >
              {p.name} ({p.taskCount})
            </button>
          ))}
        </div>
        <div className="row" style={{ marginTop: 12 }}>
          {(["list", "board", "gantt"] as View[]).map((v) => (
            <button key={v} className={`btn ${view === v ? "primary" : ""}`} onClick={() => setView(v)}>
              {v}
            </button>
          ))}
        </div>
      </div>

      {selected === "all" && (
        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th>Project</th>
                <th>Status</th>
                <th>Owner</th>
                <th>Window</th>
                <th>Done</th>
                <th>Progress</th>
              </tr>
            </thead>
            <tbody>
              {list.map((p) => (
                <tr key={p.id}>
                  <td>
                    <strong>{p.name}</strong>
                    <div>{p.description}</div>
                  </td>
                  <td><span className="badge">{p.status}</span></td>
                  <td>{p.ownerName ?? "—"}</td>
                  <td className="mono-inline">
                    {p.startDate ?? "?"} → {p.targetEndDate ?? "?"}
                  </td>
                  <td className="mono-inline">
                    {p.doneCount}/{p.taskCount}
                  </td>
                  <td className="mono-inline">{p.percentComplete}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {view === "list" && (
        <div className="card">
          <h2>Tasks</h2>
          <table className="table">
            <thead>
              <tr>
                <th>Task</th>
                <th>Project</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Due</th>
                <th>%</th>
                <th>Owner</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((t) => (
                <tr key={t.id}>
                  <td>
                    {t.parentTaskId && <span>↳ </span>}
                    {t.isMilestone && <span className="badge">milestone</span>} {t.title}
                    {t.dependsOn.length > 0 && (
                      <div>
                        after:{" "}
                        {t.dependsOn
                          .map((id) => all.find((x) => x.id === id)?.title ?? id)
                          .join("; ")}
                      </div>
                    )}
                  </td>
                  <td>{t.projectName}</td>
                  <td><span className="badge">{t.status}</span></td>
                  <td>{t.priority}</td>
                  <td className="mono-inline">{t.dueAt ?? "—"}</td>
                  <td className="mono-inline">{t.percentComplete}</td>
                  <td>{t.assignees.find((a) => a.role === "responsible")?.name ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {view === "board" && (
        <div className="card">
          <h2>Board</h2>
          <div className="row" style={{ alignItems: "flex-start", gap: 12, overflowX: "auto" }}>
            {COLUMNS.map((col) => {
              const items = shown.filter((t) => t.status === col);
              return (
                <div key={col} style={{ minWidth: 200, flex: 1 }}>
                  <div className="badge" style={{ marginBottom: 8 }}>
                    {col} ({items.length})
                  </div>
                  {items.map((t) => (
                    <div className="skill-card" key={t.id} style={{ marginBottom: 8 }}>
                      <strong>{t.title}</strong>
                      <div>{t.assignees.find((a) => a.role === "responsible")?.name ?? "—"}</div>
                      <div className="mono-inline">{t.dueAt ?? ""}</div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {view === "gantt" && (
        <div className="card">
          <h2>Timeline</h2>
          {dated.length === 0 ? (
            <p>No dated tasks.</p>
          ) : (
            <div>
              {dated.map((t) => {
                const s = ((+new Date(t.startAt!) - min) / span) * 100;
                const w = Math.max(1.5, ((+new Date(t.dueAt!) - +new Date(t.startAt!)) / span) * 100);
                const done = t.status === "done";
                return (
                  <div key={t.id} style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 13, marginBottom: 3 }}>
                      {t.isMilestone ? "◆ " : ""}
                      {t.title} <span className="mono-inline">({t.percentComplete}%)</span>
                    </div>
                    <div
                      style={{
                        position: "relative",
                        height: 14,
                        background: "rgba(255,255,255,.06)",
                        borderRadius: 7,
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          left: `${s}%`,
                          width: `${w}%`,
                          top: 0,
                          bottom: 0,
                          borderRadius: 7,
                          background: done ? "#10b981" : t.isMilestone ? "#f59e0b" : "#6366f1",
                          opacity: done ? 0.65 : 1,
                        }}
                        title={`${t.startAt} → ${t.dueAt}`}
                      />
                    </div>
                  </div>
                );
              })}
              <p className="mono-inline">
                {new Date(min).toISOString().slice(0, 10)} → {new Date(max).toISOString().slice(0, 10)}
              </p>
            </div>
          )}
        </div>
      )}
    </>
  );
}
